import { JSX } from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { FontSizeProvider } from "components/Resume/ResumePDF/FontSizeContext";
import { DEFAULT_FONT_COLOR, setSettings } from "lib/redux/settingsSlice";
import type { Settings, ShowForm } from "lib/redux/settingsSlice";
import type { Resume } from "lib/redux/types";
import { SuppressResumePDFErrorMessage } from "components/Resume/ResumePDF/common/SuppressResumePDFErrorMessage";
import { WorkExperience } from "./WorkExperience";
import { Education } from "./Education";
import { Project } from "./Project";
import { Skills } from "./Skills";
import { Certificate } from "./Certificate";
import { Language } from "./Language";
import { Award } from "./Award";
import { Custom } from "./Custom";
import { Summary } from "./Summary";

import { A4_HEIGHT_PT, LETTER_HEIGHT_PT } from "lib/constants";
import { IconType, ResumePDFIcon } from "../ResumePDF/common/ResumePDFIcon";
import { extractUsernameFromURL } from "lib/utils/urlHelpers";
import { ResumePDFLink, ResumePDFText } from "../ResumePDF/common";
import { Profile } from "./Profile";

export const ResumeTwentyOne = ({
  resume,
  settings,
  isPDF = false,
  enableHighlight = false,
  enableMistakeHighlight = false,
}: {
  resume: Resume;
  settings: Settings;
  isPDF?: boolean;
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
}) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    skills,
    certifications,
    languages,
    awards,
    custom,
  } = resume;
  const { name } = profile;
  const {
    fontFamily,
    fontSize,
    documentSize,
    formToHeading,
    formToShow,
    formsOrder,
  } = settings;

  const themeColor = settings.themeColor || DEFAULT_FONT_COLOR;

  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);
  const leftRightFormOrder = settings.leftRightFormOrder;

  const showFormsOrder = formsOrder.filter((form) => formToShow[form]);
  const leftShowFormsOrder = leftRightFormOrder?.left?.filter(
    (form) => formToShow[form]
  );
  const rightShowFormsOrder = leftRightFormOrder?.right?.filter(
    (form) => formToShow[form]
  );

  const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
    workExperiences: () => (
      <WorkExperience
        heading={formToHeading["workExperiences"]}
        workExperiences={workExperiences}
        themeColor={themeColor}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    educations: () => (
      <Education
        heading={formToHeading["educations"]}
        educations={educations}
        themeColor={themeColor}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    projects: () => (
      <Project
        heading={formToHeading["projects"]}
        projects={projects}
        themeColor={themeColor}
        isPDF={isPDF}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    skills: () => (
      <Skills
        heading={formToHeading["skills"]}
        skills={skills}
        themeColor={themeColor}
      />
    ),
    certifications: () => (
      <Certificate
        heading={formToHeading["certifications"]}
        certifications={certifications}
        themeColor={themeColor}
        isPDF={isPDF}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    languages: () => (
      <Language
        heading={formToHeading["languages"]}
        languages={languages}
        themeColor={themeColor}
        isPDF={isPDF}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    awards: () => (
      <Award
        heading={formToHeading["awards"]}
        awards={awards}
        themeColor={themeColor}
        isPDF={isPDF}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    custom: () => (
      <Custom
        heading={formToHeading["custom"]}
        custom={custom}
        themeColor={themeColor}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
  };
  const { email, phone, url1, url2, summary, location } = profile;
  const iconProps = { email, phone, location, url1, url2 };
  return (
    <>
      <Document title={`${name} Resume`} author={name} producer={"OpenResume"}>
        <Page
          size={documentSize === "A4" ? "A4" : "LETTER"}
          style={{
            ...styles.flexCol,
            width: "100vw",
            color: DEFAULT_FONT_COLOR,
            fontFamily,
            backgroundColor: "white",
            fontSize: fontSize + "pt",
            height:
              documentSize === "A4"
                ? `${A4_HEIGHT_PT}pt`
                : `${LETTER_HEIGHT_PT}pt`,
          }}
        >
          <FontSizeProvider fontSize={fontSize}>
            {/* <View style={{ ...styles.flexRow, height: `${spacing[7]}` }} fixed>
              <View
                style={{
                  width: "70%", // Left side width set to 70%
  backgroundColor:themeColor,
                  paddingLeft: spacing[7],
                paddingRight: spacing[7],
              }}
            ></View>
            <View
              style={{
                width: "30%", // Right side width set to 30%
                backgroundColor:themeColor,
                paddingLeft: spacing[7],
                paddingRight: spacing[7],
              }}
            ></View>
          </View> */}
          <View
            style={{
              ...styles.flexCol,
              alignItems: "center", // Center the Profile component horizontally
              // paddingTop: spacing[5], // Optional padding above Profile if needed
            }}
          >
            <Profile profile={profile} themeColor={themeColor} isPDF={isPDF} enableHighlight={enableHighlight} />
          </View>

          <View
            style={{
              ...styles.flexRow,
              marginTop: `-${spacing[0.5]}`,
            }}
          >
            <View
              style={{
                width: "70%",

                padding: `${spacing[2]} ${spacing[7]} ${spacing[0]} ${spacing[7]}`,
              }}
            >
              {profile.summary && (
                <Summary
                  heading="PROFILE"
                  summary={profile.summary}
                  themeColor={themeColor}
                  enableHighlight={enableHighlight}
                  enableMistakeHighlight={enableMistakeHighlight}
                />
              )}
              {leftShowFormsOrder?.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
            <View
              style={{
                width: "2.2px",
                backgroundColor: "#434445",
                height: "100%",
              }}
              fixed
            />
            <View
              style={{
                width: "30%",

                padding: `${spacing[2]} ${spacing[7]} ${spacing[0]} ${spacing[7]}`,
              }}
            >
              {rightShowFormsOrder?.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
          </View>

          <View
            style={{
              flex: 1,
              width: "100%",
              ...styles.flexRow,
              marginTop: `-${spacing[0.5]}`,
            }}
          >
            <View
              style={{
                width: "70%",

                paddingLeft: spacing[7],
                paddingRight: spacing[7],
              }}
            ></View>
            <View
              style={{
                width: "2.2px",
                backgroundColor: "#434445",
                height: "100%",
              }}
              fixed
            />
            <View
              style={{
                width: "30%",

                paddingLeft: spacing[7],
                paddingRight: spacing[7],
              }}
            ></View>
          </View>
          <View
            style={{
              ...styles.flexRow,
              height: `${spacing[5]}`,
              marginTop: `-${spacing[0.5]}`,
            }}
            fixed
          >
            <View
              style={{
                width: "70%",

                paddingLeft: spacing[7],
                paddingRight: spacing[7],
              }}
            ></View>
            <View
              style={{
                width: "2.2px",
                backgroundColor: "#434445",
                height: "100%",
              }}
              fixed
            />
            <View
              style={{
                width: "30%",

                paddingLeft: spacing[7],
                paddingRight: spacing[7],
              }}
            ></View>
          </View>
          </FontSizeProvider>
        </Page>
      </Document>
      <SuppressResumePDFErrorMessage />
    </>
  );
};
